import { Users, LogIn, LogOut, AlertTriangle, Timer } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { motifLabel } from "@/lib/format";

const KPI = ({ icon: Icon, label, value, accent, testid }) => (
  <div
    className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-4 animate-fade-in-up"
    data-testid={testid}
  >
    <div className={`h-12 w-12 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="h-6 w-6" />
    </div>
    <div className="min-w-0">
      <div className="font-serif-title text-3xl font-bold text-[#0A192F] tabular-nums leading-none">
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-1 leading-tight">{label}</div>
    </div>
  </div>
);

const CHART_COLORS = ["#0A192F", "#D4AF37", "#1E5F8C", "#2E7D6F", "#B4472E", "#6B4FA0", "#C59B27", "#3E7CB1"];

const Panel = ({ title, children, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={testid}>
    <h3 className="font-serif-title text-base font-bold text-[#0A192F] mb-4">{title}</h3>
    {children}
  </div>
);

export const Dashboard = ({ stats, visitors, filterLabel }) => {
  if (!stats) return null;

  // Répartition par personne visitée (basé sur le filtre actif)
  const byPerson = {};
  const byMotif = {};
  visitors.forEach((v) => {
    byPerson[v.personne_visitee] = (byPerson[v.personne_visitee] || 0) + 1;
    const m = motifLabel(v);
    byMotif[m] = (byMotif[m] || 0) + 1;
  });
  const personData = Object.entries(byPerson)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
  const motifData = Object.entries(byMotif).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KPI icon={Users} label="Actuellement sur site" value={stats.on_site} accent="bg-[#0A192F] text-[#D4AF37]" testid="kpi-onsite" />
        <KPI icon={LogIn} label="Arrivées aujourd'hui" value={stats.arrivals_today} accent="bg-emerald-50 text-emerald-600" testid="kpi-arrivals" />
        <KPI icon={LogOut} label="Départs aujourd'hui" value={stats.departures_today} accent="bg-slate-100 text-slate-600" testid="kpi-departures" />
        <KPI icon={AlertTriangle} label="Présences > 4h" value={stats.overstay} accent="bg-red-50 text-red-600" testid="kpi-overstay" />
        <KPI icon={Timer} label="Durée moy. (min)" value={stats.avg_duration_min} accent="bg-[#F7EBAA] text-[#8B6B1B]" testid="kpi-duration" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <Panel title={`Répartition par personne / service visité — ${filterLabel}`} testid="chart-by-person">
            {personData.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={personData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Visites">
                    {personData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 py-12 text-center">Aucune donnée pour ce filtre.</p>
            )}
          </Panel>
        </div>

        <div className="lg:col-span-5">
          <Panel title="Répartition par motif" testid="chart-by-motif">
            {motifData.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={motifData} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={90} label>
                    {motifData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 py-12 text-center">Aucune donnée pour ce filtre.</p>
            )}
          </Panel>
        </div>
      </div>

      <Panel title="Évolution des visites — 7 derniers jours" testid="chart-weekly">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={stats.weekly} margin={{ left: 0, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#0A192F" strokeWidth={3} dot={{ r: 4, fill: "#D4AF37" }} name="Visites" />
          </LineChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
};
